import React, { useState, useEffect } from "react";
import { Modal, Button, Form, InputGroup, Alert, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt, faSearch, faCrosshairs } from "@fortawesome/free-solid-svg-icons";
import { db } from "../../firebase"; // Adjusted path
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import axios from "axios";

const LocationPopup = ({ show, onHide }) => {
    const [pincode, setPincode] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [manualInput, setManualInput] = useState("");

    // Popup must always be closeable — never block the user
    const handleClose = () => {
        if (onHide) onHide();
    };

    const validatePincode = async (code) => {
        setLoading(true);
        setError("");
        try {
            const locationsRef = collection(db, "available_locations");

            // 1. Check if document exists with ID = code
            const docRef = doc(db, "available_locations", code);
            const docSnap = await getDoc(docRef);

            let isValid = false;

            if (docSnap.exists()) {
                isValid = true;
            } else {
                // 2. Fallback: Try finding by field 'pincode'
                const q = query(locationsRef, where("pincode", "==", code));
                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                    isValid = true;
                }
            }

            if (isValid) {
                localStorage.setItem("delivery_pincode", code);
                setPincode(code);
                setSuccess("Yes, we are delivering you product soon");
                setTimeout(() => {
                    handleClose(); // Close the modal
                    window.location.reload();
                }, 2000);
            } else {
                setError("This area is not serviceable yet. You can still browse our products.");
                // Auto-close after 2 s — user is never blocked
                setTimeout(() => {
                    handleClose();
                }, 2000);
            }
        } catch (err) {
            console.error("Validation Error:", err);
            setError("Failed to validate location. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleDetectLocation = () => {
        setLoading(true);
        setError("");
        if (!navigator.geolocation) {
            setError("Geolocation is not supported by your browser.");
            setLoading(false);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                try {
                    // Use OpenStreetMap Nominatim for reverse geocoding (Free, no key needed)
                    const response = await axios.get(
                        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
                    );

                    const address = response.data.address;
                    const detectedPincode = address.postcode;

                    if (detectedPincode) {
                        setManualInput(detectedPincode); // Pre-fill input
                        validatePincode(detectedPincode);
                    } else {
                        setError("Could not detect pincode from your location.");
                    }
                } catch (err) {
                    console.error("Geocoding Error:", err);
                    setError("Failed to fetch location details.");
                } finally {
                    setLoading(false);
                }
            },
            (err) => {
                console.error("Geolocation Error:", err);
                setError("Unable to retrieve your location. Please ensure permission is granted.");
                setLoading(false);
            }
        );
    };

    const handleManualSubmit = (e) => {
        e.preventDefault();
        if (!manualInput || manualInput.length < 6) {
            setError("Please enter a valid 6-digit pincode.");
            return;
        }
        validatePincode(manualInput);
    };

    return (
        <>
            <style>
                {`
                    @media (min-width: 992px) {
                        .location-popup-modal {
                            margin: 0;
                            margin-top: 75px; /* Below Navbar */
                            margin-left: 150px; /* Align with logo/location area */
                            max-width: 600px;
                        }
                    }
                    /* Keep centered on mobile */
                    @media (max-width: 991px) {
                        .location-popup-modal {
                            margin: 1.75rem auto;
                        }
                    }
                `}
            </style>
            <Modal
                show={show}
                onHide={handleClose}
                animation={false}
                backdrop={true}
                keyboard={true}
                dialogClassName="location-popup-modal"
                contentClassName="border-0 rounded-4 overflow-hidden shadow-lg"
            >
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold fs-5 w-100">
                        Change Location
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4 pt-2">
                    <p className="text-muted small mb-4">
                        Tag your location to ensure seamless delivery.
                    </p>

                    {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
                    {success && <Alert variant="success" className="py-2 small">{success}</Alert>}

                    <div className="d-flex flex-column flex-md-row align-items-md-center gap-3">
                        {/* Detect Location Button - Green */}
                        <Button
                            variant="success"
                            className="py-2 px-4 fw-bold d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ backgroundColor: '#0c831f', borderColor: '#0c831f', minWidth: '180px' }} // Blinkit Green
                            onClick={handleDetectLocation}
                            disabled={loading}
                        >
                            {loading ? <Spinner animation="border" size="sm" className="me-2" /> : <FontAwesomeIcon icon={faCrosshairs} className="me-2" />}
                            {loading ? "Detecting..." : "Detect my location"}
                        </Button>

                        {/* OR Separator */}
                        <div className="d-flex align-items-center text-muted small fw-bold mx-2">
                            <span className="bg-light px-2 rounded-circle border">OR</span>
                        </div>

                        {/* Search Input */}
                        <Form onSubmit={handleManualSubmit} className="flex-grow-1">
                            <InputGroup>
                                <InputGroup.Text className="bg-white border-end-0 ps-3">
                                    <FontAwesomeIcon icon={faSearch} className="text-muted" />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Type your PINCODE"
                                    value={manualInput}
                                    onChange={(e) => setManualInput(e.target.value)}
                                    disabled={loading}
                                    className="border-start-0 shadow-none"
                                    maxLength={6}
                                />
                                {/* Hidden submit button to allow Enter key submission */}
                                <Button type="submit" hidden disabled={loading}></Button>
                            </InputGroup>
                        </Form>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default LocationPopup;
